import type { RouteLocationNormalized, RouteLocationRaw, Router } from 'vue-router'

import { CHECKIN_REQUIRED } from '@/app/api/client'
import { useHostStore } from '@/app/host/store'
import { useSessionStore } from '@/stores/session'

/**
 * Where a signed-in user should land, given what the server says about them.
 * Pure so it can be unit-tested; the guard below just applies it.
 */
export function landingFor(
  session: {
    isAuthenticated: boolean
    securityStep: 'password' | 'mfa' | null
    isPlatformOperator: boolean
    tenantId: string | null
    activeMemberships: { tenant_id: string }[]
  },
  pinnedTenantId: string | null = null,
): RouteLocationRaw {
  if (!session.isAuthenticated) return { name: 'login' }
  if (session.securityStep) return { name: 'security-setup' }
  const memberOf = session.activeMemberships
  // BRD-050: a tenant address never offers a choice - this workspace or nothing
  if (pinnedTenantId) {
    return memberOf.some((m) => m.tenant_id === pinnedTenantId)
      ? { name: 'tenant-dashboard' }
      : { name: 'no-workspace' }
  }
  if (session.tenantId && memberOf.some((m) => m.tenant_id === session.tenantId)) {
    return { name: 'tenant-dashboard' }
  }
  if (memberOf.length === 1 && !session.isPlatformOperator) {
    return { name: 'tenant-dashboard', query: { tenant: memberOf[0]?.tenant_id } }
  }
  if (memberOf.length === 0 && session.isPlatformOperator) return { name: 'platform-dashboard' }
  return { name: 'select-tenant' }
}

/** Screens open to an employee who has not checked in yet (BRD-077). */
const CHECKIN_OPEN_ROUTES = new Set(['attendance', 'account-security', 'notifications'])
/** Screens open to a lead whose team is not signed in yet (BRD-079). */
const TEAM_SIGNIN_OPEN_ROUTES = new Set([...CHECKIN_OPEN_ROUTES, 'attendance-team'])

export function installGuards(router: Router): void {
  window.addEventListener(CHECKIN_REQUIRED, () => {
    const session = useSessionStore()
    session.checkinRequired = true
    if (router.currentRoute.value.name !== 'attendance') {
      void router.push({ name: 'attendance', query: { gate: '1' } })
    }
  })
  router.beforeEach(async (to: RouteLocationNormalized) => {
    const session = useSessionStore()
    const host = useHostStore()
    if (!host.loaded) await host.load()
    const pinned = host.tenant?.id ?? null
    if (host.unknown && to.name !== 'no-workspace') return { name: 'no-workspace' }
    if (pinned && session.tenantId !== pinned) session.selectTenant(pinned)
    if (!session.loaded) {
      try {
        await session.load()
      } catch {
        // Offline or the API is down: let the route render; screens show their own
        // offline/error state (UI/UX §1).
      }
    }

    if (to.meta.guestOnly && session.isAuthenticated) return landingFor(session, pinned)
    if (pinned && (to.name === 'select-tenant' || to.meta.platform)) {
      return landingFor(session, pinned)
    }
    if (pinned && session.isAuthenticated && to.meta.tenant && !session.tenant) {
      return { name: 'no-workspace' }
    }

    if (to.meta.auth && !session.isAuthenticated) {
      return { name: 'login', query: { next: to.fullPath } }
    }

    if (session.isAuthenticated && session.securityStep && !to.meta.allowPendingSecurity) {
      return { name: 'security-setup', query: { next: to.fullPath } }
    }

    if (to.meta.tenant) {
      const wanted = typeof to.query.tenant === 'string' ? to.query.tenant : null
      if (wanted && session.reachableTenants.some((m) => m.tenant_id === wanted)) {
        session.selectTenant(wanted)
        const query = { ...to.query }
        delete query.tenant
        return { ...to, query }
      }
      if (!session.tenant) return { name: 'select-tenant', query: { next: to.fullPath } }
      // Screens read the grant summary synchronously on mount; have it before they render.
      if (session.grantsLoadedFor !== session.tenantId) {
        try {
          await session.loadGrants()
        } catch {
          /* the screen reports API errors itself */
        }
      }
    }

    // BRD-077: an employee who must check in first sees only check-in (and their own
    // security settings and notifications) until the server says they have.
    if (
      to.meta.tenant &&
      session.checkinRequired &&
      !CHECKIN_OPEN_ROUTES.has(String(to.name ?? ''))
    ) {
      return { name: 'attendance', query: { gate: '1' } }
    }

    // BRD-079: a checked-in lead whose team still needs signing in goes to Team sign-in
    if (
      to.meta.tenant &&
      session.teamSigninPending &&
      !TEAM_SIGNIN_OPEN_ROUTES.has(String(to.name ?? ''))
    ) {
      return { name: 'attendance-team', query: { pending: '1' } }
    }

    if (to.meta.platform && !session.isPlatformOperator) return landingFor(session, pinned)

    // Permission-aware routing (UI/UX §Role-Aware Dashboards and Navigation): a URL the
    // caller's effective grants do not cover stops at a clean "no access" page before any
    // data is requested. UX only - every API re-checks permission and scope.
    if (to.meta.permissions?.length && session.grantsLoadedFor === session.tenantId) {
      const asLead = Boolean(to.meta.teamLead && session.leadsTeam) // BRD-089
      if (
        (!asLead && !to.meta.permissions.some((p) => session.can(p))) ||
        !(to.meta.requires ?? []).every((p) => session.can(p))
      ) {
        return { name: 'forbidden', query: { from: to.fullPath } }
      }
    }
    if (to.meta.platformPermissions?.length) {
      const held = session.me?.platform_permissions ?? []
      if (!to.meta.platformPermissions.some((p) => held.includes(p))) {
        return { name: 'platform-forbidden', query: { from: to.fullPath } }
      }
    }

    return true
  })

  router.afterEach((to) => {
    const title = to.meta.title
    document.title = title ? `${title} · Fumbo ERP` : 'Fumbo ERP'
  })
}
